import { useState } from 'react'
import { Users, Search, MapPin } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/Sheet'
import { Button } from '@/components/ui/Button'
import { SidebarInput } from '@/components/ui/shadcn/sidebar'
import { VisitorListItem } from './VisitorListItem'
import type { Visitor, PresenceStats } from './types'

interface MobileVisitorMenuProps {
  visitors: Visitor[]
  selectedId: string | null
  onSelect: (visitor: Visitor) => void
  onStartConversation?: (visitor: Visitor) => void
  presenceStats: PresenceStats
}

export function MobileVisitorMenu({
  visitors,
  selectedId,
  onSelect,
  onStartConversation,
  presenceStats,
}: MobileVisitorMenuProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredVisitors = searchQuery
    ? visitors.filter(
        (v) =>
          v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.location?.city?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : visitors

  const handleSelect = (visitor: Visitor) => {
    onSelect(visitor)
    setOpen(false)
  }

  const handleStartConversation = (visitor: Visitor) => {
    onStartConversation?.(visitor)
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="md:hidden absolute top-3 right-3 z-10 bg-card/80 backdrop-blur-sm"
        >
          <Users className="size-4" />
          <span className="sr-only">Open visitor list</span>
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-full sm:max-w-md p-0 flex flex-col bg-sidebar-secondary text-sidebar-secondary-foreground"
      >
        <SheetHeader className="p-4 border-b border-sidebar-secondary-accent/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="size-4 text-primary" />
              <SheetTitle className="text-sidebar-secondary-foreground">
                Visitors
              </SheetTitle>
            </div>
            <div className="text-xs text-sidebar-secondary-muted">
              {presenceStats.live_count} live / {presenceStats.active_count}{' '}
              active
            </div>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-2.5 top-2.5 size-4 text-sidebar-secondary-muted" />
            <SidebarInput
              placeholder="Search visitors..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {filteredVisitors.length === 0 ? (
            <EmptyState searchQuery={searchQuery} />
          ) : (
            filteredVisitors.map((visitor) => (
              <VisitorListItem
                key={visitor.id}
                visitor={visitor}
                isSelected={selectedId === visitor.id}
                onClick={() => handleSelect(visitor)}
                onStartConversation={() => handleStartConversation(visitor)}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function EmptyState({ searchQuery }: { searchQuery: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center px-4">
      <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-3">
        <MapPin className="size-6 text-primary" />
      </div>
      <p className="text-sm text-sidebar-secondary-muted">
        {searchQuery ? 'No visitors found' : 'No visitors with locations yet'}
      </p>
    </div>
  )
}
